import { Capacitor, SystemBarType, SystemBars, SystemBarsStyle } from "@capacitor/core"
import { SafeArea, SystemBarsType } from "@capacitor-community/safe-area"
import { useStorage } from "@vueuse/core"
import { DarkTheme } from "@/enums/theme-choices"

function isDarkTheme(storedTheme: DarkTheme): boolean {
  if (storedTheme === DarkTheme.DARK) return true
  if (storedTheme === DarkTheme.SYSTEM) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
  }
  return false
}

// timeout voodoo to avoid flickering and race conditions around splash screen and system bar updates
export async function initializeStatusBar(_userLoggedIn: boolean, attempts = 1) {
  await new Promise((resolve) => setTimeout(resolve, 100))

  const storedTheme = useStorage<DarkTheme>("AppSettingsTheme", DarkTheme.LIGHT)
  const navigationBarStyle = isDarkTheme(storedTheme.value)
    ? SystemBarsStyle.Dark
    : SystemBarsStyle.Light

  // Status bar sits on navy (splash + primary top inset). Dark = white icons.
  await setSystemBarsStyle(SystemBarsStyle.Dark, navigationBarStyle)

  if (attempts < 5) {
    setTimeout(() => {
      initializeStatusBar(_userLoggedIn, attempts + 1)
    }, 100)
  }
}

async function setSystemBarsStyle(
  statusBarStyle: SystemBarsStyle,
  navigationBarStyle: SystemBarsStyle
) {
  const updates: Promise<void>[] = [
    // Capacitor 8 SystemBars owns the native default; keep it in sync so a later
    // config-change callback does not restore DEFAULT (dark icons on a light-mode phone).
    SystemBars.setStyle({
      bar: SystemBarType.StatusBar,
      style: statusBarStyle
    }),
    SafeArea.setSystemBarsStyle({
      type: SystemBarsType.StatusBar,
      style: statusBarStyle
    })
  ]

  // iOS SystemBars.setStyle ignores `bar` and would overwrite the status bar
  // if we also sent Light for the navigation bar.
  if (Capacitor.getPlatform() === "android") {
    updates.push(
      SystemBars.setStyle({
        bar: SystemBarType.NavigationBar,
        style: navigationBarStyle
      }),
      SafeArea.setSystemBarsStyle({
        type: SystemBarsType.NavigationBar,
        style: navigationBarStyle
      })
    )
  }

  await Promise.all(updates)
}
